import { useState } from "react";
import { CreditCard } from "lucide-react";

const encode = (data) =>
  Object.keys(data).map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(data[k])).join("&");

// Adult / junior membership application form, used on the Contact and Payments pages.
// onPayment (optional) is called with the member's details when they go to pay by card or submit for BACS.
export default function MembershipApplication({ adultLabel = "Adult", onPayment }) {
  const [membershipTab, setMembershipTab] = useState("adult");

  const [memberName, setMemberName]       = useState("");
  const [memberAddress, setMemberAddress] = useState("");
  const [memberEmail, setMemberEmail]     = useState("");
  const [memberMobile, setMemberMobile]   = useState("");
  const [rulesAgreed, setRulesAgreed]     = useState(false);
  const [bacsSubmitted, setBacsSubmitted] = useState(false);

  const [juniorName, setJuniorName]               = useState("");
  const [juniorDob, setJuniorDob]                 = useState("");
  const [juniorAddress, setJuniorAddress]         = useState("");
  const [juniorMobile, setJuniorMobile]           = useState("");
  const [parentName, setParentName]               = useState("");
  const [parentEmail, setParentEmail]             = useState("");
  const [parentMobile, setParentMobile]           = useState("");
  const [juniorRulesAgreed, setJuniorRulesAgreed] = useState(false);
  const [bacsJuniorSubmitted, setBacsJuniorSubmitted] = useState(false);

  const currentYear = new Date().getFullYear();
  const juniorAgeOnJan1 = juniorDob ? (() => {
    const jan1  = new Date(currentYear, 0, 1);
    const birth = new Date(juniorDob);
    let age = jan1.getFullYear() - birth.getFullYear();
    const m = jan1.getMonth() - birth.getMonth();
    if (m < 0 || (m === 0 && jan1.getDate() < birth.getDate())) age--;
    return age;
  })() : null;
  const juniorEligible = juniorAgeOnJan1 !== null && juniorAgeOnJan1 <= 16;

  const membershipReady = memberName.trim() && memberAddress.trim() && memberEmail.trim() && memberMobile.trim() && rulesAgreed;
  const juniorMembershipReady = juniorName.trim() && juniorDob && juniorEligible && juniorAddress.trim() && parentName.trim() && parentEmail.trim() && parentMobile.trim() && juniorRulesAgreed;

  const submitMembershipToNetlify = (method) => {
    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({ "form-name": "membership", name: memberName, address: memberAddress, email: memberEmail, mobile: memberMobile, rulesAgreed: "Yes – agreed to club rules and constitution" }),
    }).catch(() => {});
    onPayment?.({ membershipType: adultLabel, name: memberName, email: memberEmail, phone: memberMobile, amount: "15.00", method });
  };

  const handleMembershipClick = (e) => {
    if (!membershipReady) { e.preventDefault(); return; }
    submitMembershipToNetlify("Card (SumUp)");
  };

  const handleBacsSubmit = () => {
    submitMembershipToNetlify("BACS");
    setBacsSubmitted(true);
  };

  const submitJuniorMembershipToNetlify = (method) => {
    fetch("/", {
      method: "POST",
      headers: { "Content-Type": "application/x-www-form-urlencoded" },
      body: encode({ "form-name": "junior-membership", name: juniorName, dob: juniorDob, address: juniorAddress, mobile: juniorMobile, parentName, parentEmail, parentMobile, rulesAgreed: "Yes – agreed to club rules and constitution" }),
    }).catch(() => {});
    onPayment?.({ membershipType: "Junior", name: juniorName, email: parentEmail, phone: parentMobile, amount: "1.00", method });
  };

  const handleJuniorMembershipClick = (e) => {
    if (!juniorMembershipReady) { e.preventDefault(); return; }
    submitJuniorMembershipToNetlify("Card (SumUp)");
  };

  const handleJuniorBacsSubmit = () => {
    submitJuniorMembershipToNetlify("BACS");
    setBacsJuniorSubmitted(true);
  };

  return (
      <div className="rounded-[2rem] border border-cyan-300/20 bg-slate-950/70 p-7 panel-lift">
        <h3 className="text-xl font-semibold text-white">Membership Application</h3>
        <p className="mt-2 text-sm text-slate-300">Please complete all details below, then proceed to payment.</p>

        <div className="mt-5 flex gap-2">
          <button type="button" onClick={() => setMembershipTab("adult")}
            className={`rounded-xl px-5 py-2.5 text-sm font-semibold transition ${membershipTab === "adult" ? "bg-cyan-400 text-slate-950" : "border border-white/10 bg-white/5 text-slate-300 hover:bg-white/10"}`}>
            {adultLabel} — £15
          </button>
          <button type="button" onClick={() => setMembershipTab("junior")}
            className={`rounded-xl px-5 py-2.5 text-sm font-semibold transition ${membershipTab === "junior" ? "bg-amber-400 text-slate-950" : "border border-white/10 bg-white/5 text-slate-300 hover:bg-white/10"}`}>
            Junior — £1
          </button>
        </div>

        {membershipTab === "adult" && (
          <>
            <div className="mt-6 grid gap-4 sm:grid-cols-2">
              <input type="text" placeholder="Full Name" value={memberName} onChange={(e) => setMemberName(e.target.value)}
                className="rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white placeholder:text-slate-300 outline-none focus:border-cyan-300/40" />
              <input type="tel" placeholder="Mobile Number" value={memberMobile} onChange={(e) => setMemberMobile(e.target.value)}
                className="rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white placeholder:text-slate-300 outline-none focus:border-cyan-300/40" />
              <textarea rows={3} placeholder="Full Address including Postcode" value={memberAddress} onChange={(e) => setMemberAddress(e.target.value)}
                className="sm:col-span-2 rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white placeholder:text-slate-300 outline-none focus:border-cyan-300/40" />
              <input type="email" placeholder="Email Address" value={memberEmail} onChange={(e) => setMemberEmail(e.target.value)}
                className="sm:col-span-2 rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white placeholder:text-slate-300 outline-none focus:border-cyan-300/40" />
            </div>
            <label className="mt-5 flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={rulesAgreed} onChange={(e) => setRulesAgreed(e.target.checked)}
                className="mt-1 h-4 w-4 shrink-0 accent-cyan-400" />
              <span className="text-sm leading-6 text-slate-300">
                I have read and agree to the{" "}
                <a href="/assets/rsaa_club_rules.pdf" target="_blank" rel="noopener noreferrer" className="text-cyan-300 underline hover:text-cyan-200">Club Rules</a>
                {" "}and the{" "}
                <a href="/assets/rsaa_constitution.pdf" target="_blank" rel="noopener noreferrer" className="text-cyan-300 underline hover:text-cyan-200">Club Constitution</a>
              </span>
            </label>
            {!membershipReady && (
              <p className="mt-3 rounded-xl border border-amber-300/20 bg-amber-400/10 px-4 py-2.5 text-sm text-amber-300">Complete all fields and agree to the Club Rules to unlock payment.</p>
            )}
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <a
                href={membershipReady ? `https://pay.sumup.com/b2c/QUNL1J1W?customer-name=${encodeURIComponent(memberName)}` : undefined}
                onClick={handleMembershipClick}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex w-full sm:w-auto items-center justify-center gap-3 rounded-2xl px-8 py-4 text-base font-semibold shadow-xl transition ${
                  membershipReady
                    ? "bg-gradient-to-r from-slate-100 to-white text-slate-950 shadow-slate-400/20 hover:scale-[1.05] hover:shadow-slate-300/30 active:scale-[0.97]"
                    : "cursor-not-allowed bg-slate-700 text-slate-400 shadow-none"
                }`}
              >
                <CreditCard className="h-5 w-5" />
                Pay £15 via Card / Bank
              </a>
            </div>
            {membershipReady && (
              <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 panel-lift">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">Pay by Bank Transfer (BACS)</p>
                <p className="mt-1 text-xs text-slate-400">Please use your name as the payment reference.</p>
                <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                  <span className="text-slate-400">Account name</span>
                  <span className="font-medium text-white">Redruth Sea Angling Association</span>
                  <span className="text-slate-400">Sort code</span>
                  <span className="font-medium text-white">30-97-00</span>
                  <span className="text-slate-400">Account number</span>
                  <span className="font-medium text-white">00184090</span>
                </div>
                <div className="mt-4 border-t border-white/10 pt-4">
                  {bacsSubmitted ? (
                    <p className="text-sm font-medium text-cyan-300">✓ Your details have been submitted — please complete your bank transfer.</p>
                  ) : (
                    <button type="button" onClick={handleBacsSubmit}
                      className="rounded-xl bg-cyan-400 px-5 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-cyan-300">
                      Submit my details for BACS payment
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}

        {membershipTab === "junior" && (
          <>
            <p className="mt-5 text-xs font-semibold uppercase tracking-[0.2em] text-amber-300/80">Junior Member Details</p>
            <p className="mt-2 text-sm text-slate-400">Junior membership is open to members aged <span className="font-semibold text-amber-300">under 16 on 1st January {currentYear}</span>.</p>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <input type="text" placeholder="Junior's Full Name" value={juniorName} onChange={(e) => setJuniorName(e.target.value)}
                className="rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white placeholder:text-slate-300 outline-none focus:border-amber-300/40" />
              <input type="tel" placeholder="Junior's Mobile (if applicable)" value={juniorMobile} onChange={(e) => setJuniorMobile(e.target.value)}
                className="rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white placeholder:text-slate-300 outline-none focus:border-amber-300/40" />
              <div className="sm:col-span-2">
                <label className="mb-1.5 block text-xs text-slate-400">Junior's Date of Birth</label>
                <input type="date" value={juniorDob} max={new Date().toISOString().split("T")[0]}
                  onChange={(e) => setJuniorDob(e.target.value)}
                  className="w-full rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white outline-none focus:border-amber-300/40 [color-scheme:dark]" />
                {juniorDob && (
                  <p className={`mt-2 text-sm font-medium ${juniorEligible ? "text-green-400" : "text-red-400"}`}>
                    {juniorEligible
                      ? `Age ${juniorAgeOnJan1} on 1st January ${currentYear} — eligible for junior membership.`
                      : `Age ${juniorAgeOnJan1} on 1st January ${currentYear} — not eligible for junior membership.`}
                  </p>
                )}
              </div>
              <textarea rows={3} placeholder="Full Address including Postcode" value={juniorAddress} onChange={(e) => setJuniorAddress(e.target.value)}
                className="sm:col-span-2 rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white placeholder:text-slate-300 outline-none focus:border-amber-300/40" />
            </div>
            <p className="mt-5 text-xs font-semibold uppercase tracking-[0.2em] text-amber-300/80">Parent / Guardian Details</p>
            <div className="mt-3 grid gap-4 sm:grid-cols-2">
              <input type="text" placeholder="Parent / Guardian Full Name" value={parentName} onChange={(e) => setParentName(e.target.value)}
                className="rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white placeholder:text-slate-300 outline-none focus:border-amber-300/40" />
              <input type="tel" placeholder="Parent / Guardian Mobile" value={parentMobile} onChange={(e) => setParentMobile(e.target.value)}
                className="rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white placeholder:text-slate-300 outline-none focus:border-amber-300/40" />
              <input type="email" placeholder="Parent / Guardian Email Address" value={parentEmail} onChange={(e) => setParentEmail(e.target.value)}
                className="sm:col-span-2 rounded-2xl border border-white/10 bg-slate-900/80 px-4 py-3 text-white placeholder:text-slate-300 outline-none focus:border-amber-300/40" />
            </div>
            <label className="mt-5 flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={juniorRulesAgreed} onChange={(e) => setJuniorRulesAgreed(e.target.checked)}
                className="mt-1 h-4 w-4 shrink-0 accent-amber-400" />
              <span className="text-sm leading-6 text-slate-300">
                I (parent/guardian) have read and agree to the{" "}
                <a href="/assets/rsaa_club_rules.pdf" target="_blank" rel="noopener noreferrer" className="text-amber-300 underline hover:text-amber-200">Club Rules</a>
                {" "}and the{" "}
                <a href="/assets/rsaa_constitution.pdf" target="_blank" rel="noopener noreferrer" className="text-amber-300 underline hover:text-amber-200">Club Constitution</a>
                {" "}on behalf of the junior member.
              </span>
            </label>
            {!juniorMembershipReady && (
              <p className="mt-3 rounded-xl border border-amber-300/20 bg-amber-400/10 px-4 py-2.5 text-sm text-amber-300">Complete all fields and agree to the Club Rules to unlock payment.</p>
            )}
            <div className="mt-5 flex flex-col gap-3 sm:flex-row">
              <a
                href={juniorMembershipReady ? `https://pay.sumup.com/b2c/QTGBECUF?customer-name=${encodeURIComponent(juniorName)}` : undefined}
                onClick={handleJuniorMembershipClick}
                target="_blank"
                rel="noopener noreferrer"
                className={`flex w-full sm:w-auto items-center justify-center gap-3 rounded-2xl px-8 py-4 text-base font-semibold shadow-xl transition ${
                  juniorMembershipReady
                    ? "bg-gradient-to-r from-slate-100 to-white text-slate-950 shadow-slate-400/20 hover:scale-[1.05] hover:shadow-slate-300/30 active:scale-[0.97]"
                    : "cursor-not-allowed bg-slate-700 text-slate-400 shadow-none"
                }`}
              >
                <CreditCard className="h-5 w-5" />
                Pay £1 via Card / Bank
              </a>
            </div>
            {juniorMembershipReady && (
              <div className="mt-4 rounded-2xl border border-white/10 bg-white/5 px-5 py-4 panel-lift">
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-amber-300">Pay by Bank Transfer (BACS)</p>
                <p className="mt-1 text-xs text-slate-400">Please use the junior member's name as the payment reference.</p>
                <div className="mt-3 grid grid-cols-2 gap-x-6 gap-y-1 text-sm">
                  <span className="text-slate-400">Account name</span>
                  <span className="font-medium text-white">Redruth Sea Angling Association</span>
                  <span className="text-slate-400">Sort code</span>
                  <span className="font-medium text-white">30-97-00</span>
                  <span className="text-slate-400">Account number</span>
                  <span className="font-medium text-white">00184090</span>
                </div>
                <div className="mt-4 border-t border-white/10 pt-4">
                  {bacsJuniorSubmitted ? (
                    <p className="text-sm font-medium text-amber-300">✓ Your details have been submitted — please complete your bank transfer.</p>
                  ) : (
                    <button type="button" onClick={handleJuniorBacsSubmit}
                      className="rounded-xl bg-amber-400 px-5 py-2.5 text-sm font-semibold text-slate-950 transition hover:bg-amber-300">
                      Submit my details for BACS payment
                    </button>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
  );
}
